import { Injectable, Injector } from '@angular/core';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { CurrentUser } from "../../common/providers/current-user";
import { GuestsProvider } from "../../guests/providers/guests-provider";
import { Team } from "../../common/models/team";
import { mapTeamRightsToRowRights, TeamItemRightsList, TeamRights } from "./teams-rights";
import { AthleteTeam } from "../../common/models/athlete-team";
import { CoachTeam } from "../../common/models/coach-team";
import { map, switchMap } from "rxjs/operators";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { AthleteGuest } from "../../common/models/athlete-guest";
import { User } from "../../common/models/user";
import { Coach } from "../../common/models/coach";
import { Athlete } from "../../common/models/athlete";
import { forkJoin, Observable } from "rxjs";
import { OrganizationRole } from "../../common/models/organization-role";
import { UsersProvider } from "../../users/providers/users-provider";
import { CoachesProvider } from "../../coaches/providers/coaches-provider";
import { AthleteRow, AthletesProvider } from "../../athletes/providers/athletes-provider";
import { Paginator } from "../../common/models/paginator";

@Injectable({providedIn: 'root'})
export class TeamsProvider {
    private teamRights: TeamRights = new TeamRights(this.utilsProvider);


    private _athletesProvider: AthletesProvider | null = null;
    private get athletesProvider(): AthletesProvider {
      if (!this._athletesProvider) {
        this._athletesProvider = this.injector.get(AthletesProvider);
      }
      return this._athletesProvider;
    }

    constructor(
        private injector: Injector,
        private utilsProvider: UtilsProvider,
        private api: ApiBaseService,
        private _currentUser: CurrentUser,
        private _currentUserRoles: CurrentUserRolesProvider,
    ) {
    }

    public get(organizationId:number, userId: number): Observable<Team> {
      return this.api.call('get', `organizations/${organizationId}/teams/${userId}`, {}, this._currentUser.getAccessToken())
        .pipe(
          map((data: any) => {
            return Team.fromAPI(data);
          })
        );
    }

    public getTeams({organizationId, pageNumber = 1, sortOrder = 'asc'}:{organizationId: number, pageNumber?: number, sortOrder?: string}): Observable<Team[]> {
      return this.api.call('get', `organizations/${organizationId}/teams/`, {
        page: pageNumber,
        sort: sortOrder,
        per_page: 0,
      }, this._currentUser.getAccessToken()).pipe(
        map((result: Paginator) => Team.fromAPIList(result.data))
      );
    }

    public createTeamRow(organizationId: number, team: Team): TeamRow {
      return this._teamRow(team, organizationId);
    }

    private _teamRow(team: Team, organizationId: number, athleteUsers?: User[]): TeamRow {
      if (!athleteUsers) {
        athleteUsers = team.coaches?.flatMap((coach: Coach) => {
          return coach.athletes?.map((athlete: Athlete) => {
            if (!athlete.user) return {} as User;
            return {
              id: athlete.user.id,
              firstName: athlete.user.firstName,
              lastName: athlete.user.lastName,
              athlete: {
                id: athlete.id,
                userId: athlete.user.id,
                coaches: athlete.coaches as Coach[],
              } as Athlete,
            } as User
          }) ?? [];
        }) ?? [];
      }
      const coachUsers = team.coaches?.map((coach: Coach) => {
          if (!coach.user) return {} as User;
          return {
            id: coach.user.id,
            firstName: coach.user.firstName,
            lastName: coach.user.lastName,
            coach: {
              id: coach.id,
              userId: coach.user.id,
              athletes: coach.athletes as Athlete[],
            } as Coach,
          } as User
        }) ?? [];
      this.teamRights.evaluateItemRight({
        user: this._currentUser.user!,
        organizationId: organizationId,
        team: team,
        rolesIds: this._currentUserRoles.getRolesIds(organizationId),
        teamCoachRelations: team.coaches?.map((coach: Coach) => {
          return {
            coachId: coach.id,
            teamId: team.id,
          } as CoachTeam;
        })??[],
        teamAthleteRelations: team.coaches?.flatMap((coach: Coach) => {
          return coach.athletes?.map((athlete: Athlete) => {
            return {
              athleteId: athlete.id,
              teamId: team.id,
            } as AthleteTeam;
          })??[];
        })??[],
        teamGuestRelations: [],
        teams: [team],
        coaches: coachUsers,
        athletes: athleteUsers,
        guests: [],
      });

      return {
        team: team,
        coaches: coachUsers,
        athletes: athleteUsers,
        coachNames: team.coaches?.map((coach: Coach) => `${coach.user?.firstName} ${coach.user?.lastName}`).join(', ') ?? '',
        removeAthleteCoachRelations: false,
        removeAthleteGuestRelations: false,
        rights: mapTeamRightsToRowRights(team.rights ?? []),
      }
    }

    public getTeamRow(organizationId: number, teamId: number): Observable<TeamRow> {
      return this.get(organizationId, teamId).pipe(
        switchMap((team: Team) => {
          return this.athletesProvider.getAthletes(organizationId, team.id).pipe(
            map((athletes: User[]) => {
              return this._teamRow(team, organizationId, athletes);
            })
          );
        })
      );
    }

    public getTeamRows({organizationId, pageNumber, sortOrder}:{organizationId: number, pageNumber?: number, sortOrder?: string}): Observable<TeamRow[]> {
      return this.getTeams({organizationId: organizationId, pageNumber: pageNumber, sortOrder: sortOrder}).pipe(
        map((teams: Team[]) => {
          return teams.map((team: Team) => {
            return this._teamRow(team, organizationId);
          });
        })
      );
    }

    public getTeamsPaginator({organizationId, pageNumber, sortOrder, teamName}:{organizationId: number, pageNumber?: number, sortOrder?: string, teamName?: string}): Observable<Paginator> {
      return this.api.call('get', `organizations/${organizationId}/teams`, {
        page: pageNumber,
        sort: sortOrder,
        teamName: teamName,
      }, this._currentUser.getAccessToken()).pipe(
        map((result: Paginator) => result),
      );
    }

  /*** END NEW REQUESTS ***/
    /*** ****************************** ***/
    /*** ******************************************** ***/
    /*** ****************************** ***/
    /*** OLD REQUESTS ***/



  public getAthleteTeamRelations(): Observable<AthleteTeam[]> {
      return this.api.call('get', 'athlete-teams', {}, this._currentUser.getAccessToken()).pipe(
        map((response: any) => {
            return AthleteTeam.fromAPIList(response);
        })
      );
    }

    public getCoachTeamRelations(): Observable<CoachTeam[]> {
      return this.api.call('get', 'coach-teams', {}, this._currentUser.getAccessToken()).pipe(
        map((response: any) => {
            return CoachTeam.fromAPIList(response);
        })
      );
    }

    // public getTeamRows(organizationId: number): Observable<TeamRow[]> {
    // throw new Error('Method not implemented.');

        // return new Promise((resolve, reject) => {
        //     Promise.all([
        //             this.api.call('get', 'organizations/' + organizationId + '/users', {
        //                 roles: [OrganizationRole.ID_SUPERCOACH, OrganizationRole.ID_COACH],
        //             }, this._currentUser.getAccessToken())
        //         this._storage.select(User)
        //             .where('id').equals(OrganizationMembership, 'userId')
        //             .and('organizationId', OrganizationMembership).equals(organizationId)
        //             .and('id', OrganizationMembership).equals(OrganizationMembership_OrganizationRole, 'organizationMembershipId')
        //             .and('organizationRoleId', OrganizationMembership_OrganizationRole).in([OrganizationRole.ID_SUPERCOACH, OrganizationRole.ID_COACH])
        //             .get(),
      //             this.api.call('get', 'organizations/' + organizationId + '/users', {
      //                 roles: [OrganizationRole.ID_ATHLETE],
      //             }, this._currentUser.getAccessToken())

        //         this._storage.select(User)
        //             .where('id').equals(OrganizationMembership, 'userId')
        //             .and('organizationId', OrganizationMembership).equals(organizationId)
        //             .and('id', OrganizationMembership).equals(OrganizationMembership_OrganizationRole, 'organizationMembershipId')
        //             .and('organizationRoleId', OrganizationMembership_OrganizationRole).in([OrganizationRole.ID_ATHLETE])
        //             .get(),
        //         this._storage.select(CoachTeam).get(),
        //         this._storage.select(AthleteTeam).get(),
        //         this._storage.select(Coach).get(),
        //         this._storage.select(Athlete).get(),
        //         this._storage.select(Team).get(),
        //         this._guestsProvider.getGuestTeamRelations(),
        //         this._guestsProvider.getGuests(),
        //     ])
        //         .then((data: any[]) => {
        //
        //             const coachUsers = data[0];
        //             const athleteUsers = data[1];
        //             const coaches_teams = data[2];
        //             const athletes_teams = data[3];
        //             const coaches = data[4];
        //             const athletes = data[5];
        //             const teams = data[6];
        //             const guests_teams = data[7];
        //             const guests = data[8];
        //
        //             const rows: TeamRow[] = [];
        //             teams.forEach((team: Team) => {
        //
        //                 // évaluation des droits sur ce coach
        //                 this.teamRights.evaluateItemRight({
        //                     user: this._currentUser.user,
        //                     organizationId,
        //                     team,
        //                     rolesIds: this._currentUserRoles.getRolesIds(organizationId),
        //                     teamCoachRelations: coaches_teams,
        //                     teamAthleteRelations: athletes_teams,
        //                     teamGuestRelations: guests_teams,
        //                     teams,
        //                     coaches,
        //                     athletes,
        //                     guests,
        //                 });
        //
        //                 rows.push(this._toTeamRow(team, coachUsers, athleteUsers, coaches, athletes, coaches_teams, athletes_teams, this.teamRights.getItemRightsList()));
        //             });
        //
        //             resolve(rows);
        //         })
        //         .catch(reject);
        // });
    // }

    // public getTeamRow(organizationId: number, teamId: number): Observable<TeamRow> {
    //   throw new Error('Method not implemented.');

        // return new Promise((resolve, reject) => {
        //     Promise.all([
        //         this._storage.select(Team)
        //             .where('id').equals(teamId)
        //             .get(1),
        //         this._storage.select(User)
        //             .where('id').equals(OrganizationMembership, 'userId')
        //             .and('organizationId', OrganizationMembership).equals(organizationId)
        //             .and('id', OrganizationMembership).equals(OrganizationMembership_OrganizationRole, 'organizationMembershipId')
        //             .and('organizationRoleId', OrganizationMembership_OrganizationRole).in([OrganizationRole.ID_SUPERCOACH, OrganizationRole.ID_COACH])
        //             .get(),
        //         this._storage.select(User)
        //             .where('id').equals(OrganizationMembership, 'userId')
        //             .and('organizationId', OrganizationMembership).equals(organizationId)
        //             .and('id', OrganizationMembership).equals(OrganizationMembership_OrganizationRole, 'organizationMembershipId')
        //             .and('organizationRoleId', OrganizationMembership_OrganizationRole).in([OrganizationRole.ID_ATHLETE])
        //             .get(),
        //         this._storage.select(CoachTeam).get(),
        //         this._storage.select(AthleteTeam).get(),
        //         this._storage.select(Coach).get(),
        //         this._storage.select(Athlete).get(),
        //         this._storage.select(Team).get(),
        //         this._guestsProvider.getGuestTeamRelations(),
        //         this._guestsProvider.getGuests(),
        //     ])
        //         .then((data: any[]) => {
        //
        //             const team = data[0];
        //             const coachUsers = data[1];
        //             const athleteUsers = data[2];
        //             const coaches_teams = data[3];
        //             const athletes_teams = data[4];
        //             const coaches = data[5];
        //             const athletes = data[6];
        //             const teams = data[7];
        //             const guests_teams = data[8];
        //             const guests = data[9];
        //
        //             // évaluation des droits sur ce coach
        //             this.teamRights.evaluateItemRight({
        //                 user: this._currentUser.user,
        //                 organizationId,
        //                 team,
        //                 rolesIds: this._currentUserRoles.getRolesIds(organizationId),
        //                 teamCoachRelations: coaches_teams,
        //                 teamAthleteRelations: athletes_teams,
        //                 teamGuestRelations: guests_teams,
        //                 teams,
        //                 coaches,
        //                 athletes,
        //                 guests,
        //             });
        //
        //             resolve(this._toTeamRow(team, coachUsers, athleteUsers, coaches, athletes, coaches_teams, athletes_teams, this.teamRights.getItemRightsList()));
        //         })
        //         .catch(reject);
        // });
    // }

    public saveTeam(organizationId: number, teamRow: TeamRow): Observable<TeamRow> {
      return this.api.call('post','organizations/' + organizationId + '/teams/' + (teamRow.team.id ?? 0),
        teamRow,
        this._currentUser.getAccessToken()
      ).pipe(
        switchMap((data: any) => {
          return this.getTeamRow(organizationId, data.id);
        }),
      );
    }

    public saveTeamCoaches(organizationId: number, removeRelations: boolean, teamRow: TeamRow): Observable<TeamRow> {
      return this.api.call('post','organizations/' + organizationId + '/teams/' + (teamRow.team.id ?? 0) + '/coaches',
        {
            atr: teamRow,
            rer: removeRelations
        },
        this._currentUser.getAccessToken()
      ).pipe(
        switchMap((dataSaved: any) => {
          return this.getTeamRow(organizationId, dataSaved.team.id);
        })
      );
    }

    public saveTeamAthletes(organizationId: number, removeCoachRelations: boolean, removeGuestRelations: boolean, teamRow: TeamRow): Observable<TeamRow> {
      return this.api.call('post','organizations/' + organizationId + '/teams/' + (teamRow.team.id ?? 0) + '/athletes',
        {
          atr: teamRow,
          rer: removeCoachRelations,
          reg: removeGuestRelations
        },
        this._currentUser.getAccessToken()
      ).pipe(
        switchMap((dataSaved: any) => {
          return this.getTeamRow(organizationId, dataSaved.team.id);
        })
      );
    }

    public deleteTeam(organizationId: number, team: Team): Observable<void> {
      return this.api.call(                'delete',
        'organizations/' + organizationId + '/teams/' + team.id,
        {},
        this._currentUser.getAccessToken()
      );
    }

    private _getCoachConcatenatedNames(coacheslist: User[]): string {

        const names: string[] = [];
        for (let i = 0; i < coacheslist.length; i++) {
            names.push(coacheslist[i].firstName + ' ' + coacheslist[i].lastName);
        }

        return names.join(', ');
    }

    private _toTeamRow(team: Team, allCoacheUsers: User[], allAthleteUsers: User[], allCoaches: Coach[], allAthletes: Athlete[], coachesTeams: CoachTeam[], athletesTeams: AthleteTeam[], teamRightsList: TeamItemRightsList, removeRightRelations = false): TeamRow {

        const coachLinks = this.utilsProvider.findManyIn([team.id], coachesTeams, 'teamId');
        const coachIds = this.utilsProvider.getKeyValues(coachLinks, 'coachId');
        const coachUserItem = this.utilsProvider.findManyIn(coachIds, allCoaches, 'id');
        const coachUserIds = this.utilsProvider.getKeyValues(coachUserItem, 'userId');
        const coaches = this.utilsProvider.findManyIn(coachUserIds, allCoacheUsers, 'id');

        const athleteLinks = this.utilsProvider.findManyIn([team.id], athletesTeams, 'teamId');
        const athleteIds = this.utilsProvider.getKeyValues(athleteLinks, 'athleteId');
        const athleteItems = this.utilsProvider.findManyIn(athleteIds, allAthletes, 'id');
        const athleteUserIds = this.utilsProvider.getKeyValues(athleteItems, 'userId');
        const athletes = this.utilsProvider.findManyIn(athleteUserIds, allAthleteUsers, 'id');

        const row: TeamRow = {
            team,
            coaches,
            athletes,
            coachNames: this._getCoachConcatenatedNames(coaches),
            removeAthleteCoachRelations: removeRightRelations,
            rights: teamRightsList,
        };

        return row;
    }
}

export interface TeamRow {
    team: Team;
    coaches: User[];
    athletes: User[];
    coachNames: string;
    removeAthleteCoachRelations?: boolean;
    removeAthleteGuestRelations?: boolean;
    rights?: TeamItemRightsList;
}
